import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { MenuCategory } from '../../types';
import { menuData as initialMenuData } from '../../data/menuData';

interface MenuContextType {
  menuData: MenuCategory[];
  loading: boolean;
  error: string | null;
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [menuData, setMenuData] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as MenuCategory));
      if (data.length > 0) {
        setMenuData(data);
      } else {
        // Fallback to local menu data when Firestore collection is empty
        console.warn('No categories in Firestore, using local fallback data. Seed data via admin panel.');
        setMenuData(initialMenuData);
      }
      setLoading(false);
    }, (err) => {
      console.error('Error fetching menu:', err);
      setError(err.message);
      // Fallback to local data on error so the app remains usable
      setMenuData(initialMenuData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <MenuContext.Provider value={{ menuData, loading, error }}>
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (context === undefined) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};
